/**
 * Drill-in slide-over for a single Books & Records item, with the practitioner
 * controls: set state (held offline / N/A / revert), notes, and lock toggle.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/ui/composed/overlays/sheet";
import { Button } from "@/ui/primitives/button";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Separator } from "@/ui/primitives/separator";
import { Badge } from "@/ui/primitives/badge";
import { Lock, LockOpen, FileText, Sparkles } from "lucide-react";
import { ChangeHistory } from "../summarization/shared-panels";
import { SeverityChip, StateChip, AdequacyChip } from "./chips";
import { ItemNotesFeed } from "./ItemNotesFeed";
import { humanizeTable, humanizeCode } from "./meta";
import type { BooksRecordsItem, ItemState, AdequacyStatus } from "./types";

/** Humanize snake_case enum tokens embedded in a free-text label
 *  (e.g. "… (agreement: No_Documentation)" → "… (agreement: No Documentation)"). */
function prettyLabel(label: string): string {
  return label.replace(/[A-Za-z]+_[A-Za-z_]+/g, (m) => humanizeCode(m));
}

export function BooksAndRecordsItemPanel({
  item,
  open,
  onClose,
  onViewDocument,
  isViewer,
}: {
  item: BooksRecordsItem | null;
  open: boolean;
  onClose: () => void;
  onViewDocument?: (documentId: string) => void;
  isViewer?: boolean;
}) {
  const setItemState = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.booksAndRecords.publicMutations
        .setItemState,
    ),
  });
  const setLock = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.booksAndRecords.publicMutations.setItemLock,
    ),
  });

  const [pendingState, setPendingState] = useState<ItemState | null>(null);
  const [pendingAdequacy, setPendingAdequacy] = useState<AdequacyStatus | null>(
    null,
  );
  const [attestation, setAttestation] = useState("");
  const [naReason, setNaReason] = useState("");

  if (!item) return null;
  const canEdit = !isViewer;

  const draftState = pendingState ?? item.state;
  const draftAdequacy = pendingAdequacy ?? item.adequacyStatus;

  const saveState = () => {
    setItemState.mutate({
      itemId: item._id,
      state: draftState,
      adequacyStatus: draftAdequacy,
      attestationDetail:
        draftState === "held_offline"
          ? attestation || item.practitioner?.attestationDetail
          : undefined,
      notApplicableReason:
        draftState === "not_applicable"
          ? naReason || item.practitioner?.notApplicableReason
          : undefined,
    });
    setPendingState(null);
    setPendingAdequacy(null);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="pr-6">{item.title}</SheetTitle>
          <SheetDescription>{item.description}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <StateChip state={item.state} />
          <SeverityChip severity={item.severity} />
          <AdequacyChip status={item.adequacyStatus} />
          {item.isManualOverride && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Lock className="size-3" /> Locked
            </span>
          )}
        </div>

        {item.gapRationale && (
          <div className="mt-4 rounded-lg border bg-muted/30 p-3">
            <h4 className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase text-muted-foreground">
              <Sparkles className="size-3" />
              Agent Assessment
            </h4>
            <p className="text-sm leading-relaxed text-foreground">
              {item.gapRationale}
            </p>
          </div>
        )}

        {item.relatedEntities.length > 0 && (
          <div className="mt-4">
            <h4 className="mb-1.5 text-xs font-semibold uppercase text-muted-foreground">
              Triggered By
            </h4>
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    <th className="w-40 px-3 py-2 text-left">Source</th>
                    <th className="px-3 py-2 text-left">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {item.relatedEntities.map((e, i) => (
                    <tr key={i} className="border-b align-top last:border-0">
                      <td className="px-3 py-2.5">
                        <Badge color="blue" variant="soft" size="sm">
                          {humanizeTable(e.table)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5 leading-snug text-foreground">
                        {prettyLabel(e.label)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {item.sourceDocuments.length > 0 && (
          <div className="mt-4">
            <h4 className="mb-1.5 text-xs font-semibold uppercase text-muted-foreground">
              Documents in Morgan
            </h4>
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 text-left">Document</th>
                    <th className="px-3 py-2 text-left">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {item.sourceDocuments.map((d, i) => (
                    <tr key={i} className="border-b align-top last:border-0">
                      <td className="px-3 py-2.5">
                        <button
                          type="button"
                          onClick={() => onViewDocument?.(d.documentId)}
                          className="inline-flex items-start gap-1.5 text-left text-primary hover:underline"
                        >
                          <FileText className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                          <span className="break-all">{d.fileName}</span>
                        </button>
                      </td>
                      <td className="px-3 py-2.5 leading-snug text-muted-foreground">
                        {d.reason || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <Separator className="my-5" />

        {/* Practitioner controls */}
        <div className="space-y-4">
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
              Status
            </h4>
            <Select
              value={draftState}
              onValueChange={(v) => setPendingState(v as ItemState)}
              disabled={!canEdit}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="present">In Morgan</SelectItem>
                <SelectItem value="held_offline">
                  Held Offline (Attested)
                </SelectItem>
                <SelectItem value="outstanding">Outstanding</SelectItem>
                <SelectItem value="not_applicable">Not Applicable</SelectItem>
              </SelectContent>
            </Select>

            {draftState === "held_offline" && (
              <Textarea
                className="mt-2"
                placeholder="Where is it held? (e.g. 'GL in MYOB, held by external accountant, sighted')"
                defaultValue={item.practitioner?.attestationDetail ?? ""}
                onChange={(e) => setAttestation(e.target.value)}
                disabled={!canEdit}
              />
            )}
            {draftState === "not_applicable" && (
              <Textarea
                className="mt-2"
                placeholder="Why is this not applicable to this company?"
                defaultValue={item.practitioner?.notApplicableReason ?? ""}
                onChange={(e) => setNaReason(e.target.value)}
                disabled={!canEdit}
              />
            )}

            <div className="mt-3">
              <h4 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                Adequacy
              </h4>
              <Select
                value={draftAdequacy}
                onValueChange={(v) => setPendingAdequacy(v as AdequacyStatus)}
                disabled={!canEdit}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="inadequate">Inadequate</SelectItem>
                  <SelectItem value="unclear">Unclear</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {canEdit && (
              <Button
                size="sm"
                className="mt-3"
                onClick={saveState}
                disabled={setItemState.isPending}
              >
                Save Changes
              </Button>
            )}
          </div>

          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
              Notes
            </h4>
            <ItemNotesFeed itemId={item._id} canEdit={canEdit} />
          </div>

          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setLock.mutate({
                  itemId: item._id,
                  locked: !item.isManualOverride,
                })
              }
            >
              {item.isManualOverride ? (
                <>
                  <LockOpen className="mr-1.5 size-3.5" /> Unlock (Let Agent
                  Manage)
                </>
              ) : (
                <>
                  <Lock className="mr-1.5 size-3.5" /> Lock From Agent Edits
                </>
              )}
            </Button>
          )}
        </div>

        {item.changeHistory.length > 0 && (
          <>
            <Separator className="my-5" />
            <ChangeHistory
              history={item.changeHistory}
              title="History"
              persistId="books-records-item:history"
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
